<template>
  <IconButton
    icon="refresh"
    :ariaLabel="tooltipText"
    :tooltip="showTooltip ? tooltipText : ''"
    :size="mappedSize"
    :variant="mappedVariant"
    :loading="loading"
    :disable="disable"
    class="refresh-btn"
    @click="emit('click', $event)"
  >
    <slot />
  </IconButton>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useSharedI18n } from '@shared/core/i18n'
import IconButton from './IconButton.vue'
import type { IconButtonSize, IconButtonVariant } from './IconButton.vue'

export type RefreshButtonSize = 'xs' | 'sm' | 'md' | 'lg'
export type RefreshButtonVariant = 'default' | 'primary' | 'secondary' | 'ghost' | 'icon-only'

interface Props {
  loading?: boolean
  disable?: boolean
  size?: RefreshButtonSize
  variant?: RefreshButtonVariant
  showTooltip?: boolean
  tooltipText?: string
}

const props = withDefaults(defineProps<Props>(), {
  loading: false,
  disable: false,
  size: 'sm',
  variant: 'default',
  showTooltip: true,
})

const emit = defineEmits<{
  click: [event: Event]
}>()

const i18nStore = useSharedI18n()

const tooltipText = computed(() =>
  props.tooltipText || i18nStore.t('common.action.refresh')
)

const mappedSize = computed((): IconButtonSize => {
  if (props.size === 'xs') return 'sm'
  return props.size as IconButtonSize
})

const mappedVariant = computed((): IconButtonVariant => {
  switch (props.variant) {
    case 'primary': return 'filled'
    case 'secondary': return 'outlined'
    default: return 'standard'
  }
})

defineOptions({
  name: 'RefreshButton',
})
</script>

<style lang="scss">
// Styles are centralized in /css/components/_buttons.scss
</style>
